import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "@reduxjs/toolkit";
import { fetch_personalized_items } from "../../actions/Personalization/dispatchers";
import {
    MAX_ITEMS,
    DISCLAIMER_TEXT,
    REC_FOR_YOU_FEATURE_INSTANCE,
    REC_FOR_YOU_TITLE,
} from "./constants";
import QuestionMark from "../buttons/QuestionMark";
import Title from "../Title/component";
import LayoutFactory from "../LayoutFactory/component";
import DetailDiscoveryPlaceholder from "../../pages/Detail/components/Discovery/components/DetailDiscoveryPlaceholder/component";
import { TRENDING_SWIMLANE_LAYOUT } from "../LayoutFactory/constants";

/**
 * Selector that injects featureAttribution information to the list of contentItems, to be processed by DisplayPolicy format function.
 * See "app/components/ContentArea/library/DisplayPolicy.js" line 24.
 */
const selectRecommendationList = createSelector(
    [
        (state) => state.personalization.items,
        (_, featureAttribution) => featureAttribution,
    ],
    (items, featureAttribution) => {
        const featuredItems = [...items];
        if (featureAttribution) {
            featuredItems.feature = featureAttribution;
        }
        return featuredItems;
    }
);

/**
 * This component will fetch and display a list of recommendations.
 *
 * @param {Object} props
 * @param {string} props.title the title of the component (default is REC_FOR_YOU_TITLE aka "Recommended for you")
 * @param {number} props.itemCount the number of recommended items to fetch (default is MAX_ITEMS)
 * @param {boolean} props.shouldFetch boolean to determine if we should fetch recommended items
 * @param {Object} props.featureAttribution
 * @param {string} props.featureAttribution.name featureAttribution name (default: REC_FOR_YOU_FEATURE_NAME a.k.a. "swimlane")
 * @param {string} props.featureAttribution.instance featureaAttribution instance (default: REC_FOR_YOU_FEATURE_INSTANCE a.k.a "rec-sys")
 * @returns {JSX.Element} The Recommendations component
 */
const Recommendations = ({
    title = REC_FOR_YOU_TITLE,
    itemCount = MAX_ITEMS,
    shouldFetch = true,
    featureAttribution = {
        instance: REC_FOR_YOU_FEATURE_INSTANCE,
    },
}) => {
    const dispatch = useDispatch();
    const path = useSelector((state) => state.app.path);
    const contentId = useSelector((state) => state.app.contentId);

    const isLoaded = useSelector((state) => state.personalization.isLoaded);
    const subscribeFunction = () =>
        dispatch(fetch_personalized_items(itemCount, path, contentId));

    useEffect(() => {
        if (shouldFetch) {
            if (window.CBC?.APP?.PubSub?.subscribe) {
                window.CBC.APP.PubSub.subscribe("READ", subscribeFunction);
            }

            contentId && subscribeFunction();
        }
        return function cleanup() {
            if (window.CBC?.APP?.PubSub?.unsubscribe) {
                window.CBC.APP.PubSub.unsubscribe("READ", subscribeFunction);
            }
        };
    }, [contentId]);

    const recommendationList = useSelector((state) =>
        selectRecommendationList(state, featureAttribution)
    );

    if (!isLoaded) {
        return (
            <div className="recommendations">
                <DetailDiscoveryPlaceholder numLoadingCards={itemCount} />
            </div>
        );
    }

    if (!recommendationList || !recommendationList.length) {
        return null;
    }

    return (
        <div className="recommendations" data-testid="recommendations">
            <Title level="h2">{title}</Title>
            <QuestionMark disclaimer={DISCLAIMER_TEXT} />
            <LayoutFactory
                layout={TRENDING_SWIMLANE_LAYOUT}
                items={recommendationList}
                featureAttributionData={featureAttribution}
            />
        </div>
    );
};

export default Recommendations;
